import firebase from "firebase"
import moment from "moment"
import * as XLSX from "xlsx"

import { db } from "../firebase"
import { statusLabel } from "../../containers/enquiry/_components/activity/log"
import { ACTIVITY_TYPES, COMMUNICATION_TYPES } from "../../containers/enquiry/_components/activity/types"

/**
 * Open-enquiry export. Every document still sitting in the `enquiries`
 * collection (deleted / converted enquiries move to `enquiries_removed`, so
 * the live collection is by definition the open pipeline) created on or after
 * the start date, flattened into one row per enquiry.
 *
 * Field map follows `functions-sync/src/mappers/enquiries.ts` — the single
 * place that already enumerates the enquiry document shape. `ai_parsing` and
 * `tee_sheet` are deliberately left out: both are large nested blobs with no
 * value in a spreadsheet.
 *
 * Activity is condensed into two cells per enquiry, mirroring the split on the
 * enquiry page: client communication in "Activity", system events in
 * "Change log". Threaded replies on an activity entry are not pulled in — that
 * needs a query per activity document, which would be thousands of round trips.
 */

// Start of the export window. Everything created on or after this, up to now.
const EXPORT_START = "01-08-2026"

// Read the activity + quotes subcollections for this many enquiries at once.
const SUBCOLLECTION_CONCURRENCY = 12

// Excel's hard per-cell limit. Long activity threads get truncated with a marker.
const MAX_CELL_LENGTH = 32767

// Activity types that count as an outbound touch, matching the enquiry page's
// Lifecycle card.
const OUTBOUND_TOUCH_TYPES = ["EMAIL_SENT", "CALL_MADE", "VOICEMAIL_LEFT"]

// Top-level enquiry fields this export knows about. Anything outside this set
// is reported to the console after a run so the map can be extended.
const MAPPED_ENQUIRY_FIELDS = [
	"client",
	"agent",
	"status",
	"site",
	"inbound_channel",
	"location",
	"date_from",
	"nights",
	"rounds",
	"group_size",
	"quoted",
	"notes",
	"follow_up",
	"tags",
	"tracking",
	"metadata",
	"archived_from_stats",
	"created",
	"updated",
	// Intentionally excluded from the sheet, but known - don't flag them.
	"ai_parsing",
	"tee_sheet",
]

const dateTime = (timestamp) => (timestamp?.seconds ? moment(timestamp.seconds, "X").format("DD/MM/YYYY HH:mm") : "")
const dateOnly = (timestamp) => (timestamp?.seconds ? moment(timestamp.seconds, "X").format("DD/MM/YYYY") : "")
const millisDate = (millis) => (millis ? moment(millis).format("DD/MM/YYYY") : "")
const yesNo = (value) => (value ? "Yes" : "No")

/**
 * Run `worker` over `items` with a fixed number of parallel slots, keeping the
 * results in the original order.
 */
const mapWithConcurrency = async (items, limit, worker) => {
	const results = new Array(items.length)
	let cursor = 0

	const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
		while (cursor < items.length) {
			const index = cursor
			cursor += 1
			results[index] = await worker(items[index], index)
		}
	})

	await Promise.all(runners)
	return results
}

/**
 * Fetch a set of documents by ID, ten at a time (the v8 `in` query ceiling).
 */
const fetchDocumentsByID = async (collection, ids) => {
	const documents = {}

	for (let index = 0; index < ids.length; index += 10) {
		const batch = ids.slice(index, index + 10)
		const snapshot = await db
			.collection(collection)
			.where(firebase.firestore.FieldPath.documentId(), "in", batch)
			.get()

		snapshot.forEach((doc) => {
			documents[doc.id] = doc.data()
		})
	}

	return documents
}

/**
 * Render one activity entry as a block of text:
 *   [12/08/2026 14:32] Email sent - Lewis Newson: Sent itinerary v2
 */
const formatActivityEntry = (entry, userNames) => {
	const stamp = dateTime(entry.created) || "No date"
	const label = ACTIVITY_TYPES[entry.type]?.label || entry.type || "Activity"
	const author = entry.user ? userNames[entry.user] || "Unknown user" : "System"
	const body = (entry.body || "").trim()
	const link = entry.link_label ? ` [${entry.link_label}]` : ""

	return `[${stamp}] ${label} - ${author}${link}${body ? `: ${body}` : ""}`
}

/**
 * Join rendered entries with a blank line between each, trimming to Excel's
 * cell ceiling if a thread runs long.
 */
const joinEntries = (entries) => {
	const joined = entries.join("\n\n")
	if (joined.length <= MAX_CELL_LENGTH) return joined
	return `${joined.slice(0, MAX_CELL_LENGTH - 40)}\n\n[truncated - see the enquiry]`
}

/**
 * Render one quote from `enquiries/{id}/quotes` as a single line.
 */
const formatQuote = (quote, userNames) => {
	const state = quote.is_cancelled ? "Cancelled" : quote.is_active ? "Sent" : quote.is_draft ? "Draft" : "Quote"
	const stamp = dateTime(quote.sent_at || quote.updated || quote.created) || "No date"
	const author = quote.agent ? userNames[quote.agent] || "Unknown user" : "Unknown user"

	const parts = [`[${stamp}] ${state}`]
	if (quote.reference) parts.push(`ref ${quote.reference}`)

	const trip = [
		quote.travel_millis ? `travelling ${millisDate(quote.travel_millis)}` : null,
		quote.nights ? `${quote.nights} nights` : null,
		quote.rounds ? `${quote.rounds} rounds` : null,
		quote.group_size ? `${quote.group_size} people` : null,
	].filter(Boolean)
	if (trip.length > 0) parts.push(trip.join(" / "))

	const total = Number(quote.totals?.total_gbp) || 0
	const perPerson = Number(quote.totals?.per_person_gbp) || 0
	if (total || perPerson) parts.push(`total £${total.toLocaleString()} (£${perPerson.toLocaleString()}pp)`)
	if (quote.sell_at) parts.push(`sell £${Number(quote.sell_at).toLocaleString()}pp`)
	if (quote.totals?.margin) parts.push(`margin ${quote.totals.margin}%`)

	parts.push(`built by ${author}`)

	return parts.join(" - ")
}

/**
 * Column widths, in characters. Keeps the long free-text cells readable
 * without the reader having to resize every column by hand.
 */
const columnWidths = (headers) =>
	headers.map((header) => {
		if (["Activity", "Change log", "Quote detail"].includes(header)) return { wch: 80 }
		if (["Notes", "Enquiry link", "User agent"].includes(header)) return { wch: 45 }
		if (["Email", "Location"].includes(header)) return { wch: 28 }
		return { wch: 18 }
	})

/**
 * Pull every open enquiry created since the start date and write the
 * spreadsheet. `onProgress` receives short status strings for the UI.
 *
 * Returns the number of enquiries exported.
 */
export const exportOpenEnquiries = async (onProgress = () => null) => {
	const startMillis = moment(EXPORT_START, "DD-MM-YYYY").startOf("day").valueOf()
	const startTimestamp = firebase.firestore.Timestamp.fromMillis(startMillis)

	onProgress(`Fetching open enquiries created since ${moment(startMillis).format("DD/MM/YYYY")}...`)

	const enquirySnapshot = await db
		.collection("enquiries")
		.where("created", ">=", startTimestamp)
		.orderBy("created", "asc")
		.get()

	const enquiries = enquirySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

	if (enquiries.length === 0) {
		onProgress("No open enquiries found in that window.")
		return 0
	}

	onProgress(`Found ${enquiries.length} open enquiries. Loading clients and agents...`)

	// Clients behind the enquiries, plus every staff account so activity
	// authors, agents and quote builders can all be resolved to a name.
	const clientIDs = Array.from(new Set(enquiries.map((enquiry) => enquiry.client).filter(Boolean)))
	const [clients, userSnapshot] = await Promise.all([
		fetchDocumentsByID("clients", clientIDs),
		db.collection("users").get(),
	])

	const userNames = {}
	userSnapshot.forEach((doc) => {
		const { first_name, last_name } = doc.data()
		userNames[doc.id] = `${first_name || ""} ${last_name || ""}`.trim() || "Unnamed user"
	})

	onProgress(`Loading activity and quotes for ${enquiries.length} enquiries...`)

	let loaded = 0
	const subcollections = await mapWithConcurrency(enquiries, SUBCOLLECTION_CONCURRENCY, async (enquiry) => {
		const [activitySnapshot, quoteSnapshot] = await Promise.all([
			db.collection(`enquiries/${enquiry.id}/activity`).orderBy("created", "asc").get(),
			db.collection(`enquiries/${enquiry.id}/quotes`).get(),
		])

		loaded += 1
		if (loaded % 25 === 0) onProgress(`Loading activity and quotes... ${loaded}/${enquiries.length}`)

		const quotes = quoteSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
		quotes.sort((a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0))

		return {
			activity: activitySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
			quotes,
		}
	})

	onProgress("Building the spreadsheet...")

	const unmappedFields = new Set()
	const rows = enquiries.map((enquiry, index) => {
		const { activity, quotes } = subcollections[index]
		const client = clients[enquiry.client] || {}
		const tracking = enquiry.tracking || {}
		const metadata = enquiry.metadata || {}

		Object.keys(enquiry).forEach((field) => {
			if (field !== "id" && !MAPPED_ENQUIRY_FIELDS.includes(field)) unmappedFields.add(field)
		})

		// Client communication in one cell, everything else (system events plus
		// any type this export doesn't recognise) in the other, so no entry is lost.
		const communication = activity.filter((entry) => COMMUNICATION_TYPES.includes(entry.type))
		const systemEvents = activity.filter((entry) => !COMMUNICATION_TYPES.includes(entry.type))

		const outboundTouches = activity.filter((entry) => OUTBOUND_TOUCH_TYPES.includes(entry.type))
		const lastOutbound = outboundTouches[outboundTouches.length - 1]
		const lastActivity = activity[activity.length - 1]

		const latestQuote = quotes[quotes.length - 1]
		const createdMoment = enquiry.created?.seconds ? moment(enquiry.created.seconds, "X") : null

		return {
			"Enquiry ID": enquiry.id,
			"Enquiry link": `${window.location.origin}/enquiry/${enquiry.id}`,
			Created: dateTime(enquiry.created),
			"Age (days)": createdMoment ? moment().diff(createdMoment, "days") : "",
			Status: statusLabel(enquiry.status),
			Agent: enquiry.agent ? userNames[enquiry.agent] || "Unknown user" : "Unassigned",
			"Agent ID": enquiry.agent || "",
			Site: enquiry.site || "",
			"Inbound channel": enquiry.inbound_channel || "",
			"Archived from stats": yesNo(enquiry.archived_from_stats),

			Location: enquiry.location || "",
			"Travel date": dateOnly(enquiry.date_from),
			Nights: enquiry.nights ?? "",
			Rounds: enquiry.rounds ?? "",
			"Group size": enquiry.group_size ?? "",
			"Quoted (£)": Number(enquiry.quoted) || 0,
			"Follow up": dateOnly(enquiry.follow_up),
			Tags: (enquiry.tags || []).join(", "),
			Notes: enquiry.notes || "",

			"Client ID": enquiry.client || "",
			"First name": client.first_name || "",
			"Last name": client.last_name || "",
			Email: client.email || "",
			Phone: client.phone || "",
			"Stableford points": client.stableford_points ?? "",
			"Client since": dateOnly(client.created),

			"UTM source": tracking.utm_source || "",
			"UTM medium": tracking.utm_medium || "",
			"UTM campaign": tracking.utm_campaign || "",
			"UTM term": tracking.utm_term || "",
			"UTM content": tracking.utm_content || "",
			"UTM ID": tracking.utm_id || "",
			"Time zone": metadata.user_time_zone || "",
			"User agent": metadata.user_agent || "",

			"Activity items": activity.length,
			"Outbound touches": outboundTouches.length,
			"Last outbound touch": dateTime(lastOutbound?.created),
			"Last activity": dateTime(lastActivity?.created),
			Activity: joinEntries(communication.map((entry) => formatActivityEntry(entry, userNames))),
			"Change log": joinEntries(systemEvents.map((entry) => formatActivityEntry(entry, userNames))),

			Quotes: quotes.length,
			"Draft quotes": quotes.filter((quote) => quote.is_draft && !quote.is_cancelled).length,
			"Sent quotes": quotes.filter((quote) => quote.is_active).length,
			"Cancelled quotes": quotes.filter((quote) => quote.is_cancelled).length,
			"Latest quote total (£)": Number(latestQuote?.totals?.total_gbp) || "",
			"Quote detail": joinEntries(quotes.map((quote) => formatQuote(quote, userNames))),
		}
	})

	if (unmappedFields.size > 0) {
		console.warn("Open-enquiry export: enquiry fields with no column mapping:", Array.from(unmappedFields).join(", "))
	}

	const worksheet = XLSX.utils.json_to_sheet(rows)
	worksheet["!cols"] = columnWidths(Object.keys(rows[0]))

	const workbook = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(workbook, worksheet, "Open enquiries")

	const fileName = `open_enquiries_${moment(startMillis).format("DD-MM-YYYY")}_to_${moment().format("DD-MM-YYYY")}.xlsx`
	XLSX.writeFile(workbook, fileName)

	onProgress(`Exported ${rows.length} open enquiries to ${fileName}`)
	return rows.length
}

export const OPEN_ENQUIRY_EXPORT_START = EXPORT_START
